import type { OverlayCategory, OverlayTarget } from "@playlive/overlay-core/url-builder";
import { buildOverlayURL } from "@playlive/overlay-core/url-builder";
import { CHARITY, isTwitchEnabled } from "./const";

export const charityIsTiltify = (charity: string | undefined) => charity === CHARITY.TILTIFY;
export const charityIsTwitch = (charity: string | undefined) => charity === CHARITY.TWITCH;
export const isYes = (value: string | undefined) => value === "yes";

export function formatCurrencyNumber(num: number | string | null | undefined) {
  if (num !== null && num !== undefined && num !== "undefined") {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }
  return 0;
}

/**
 * Helper function to get an asset image URL
 * @param src
 * @returns {string} - source URL
 */
export function getAssetImageUrl(src: string) {
  return new URL(src, import.meta.url).href;
}

/**
 *
 * @param {string} pathname
 * @returns {{type: string, slug: string}}
 */
export function getTypeSlug(pathname: string) {
  const [type, slug] = pathname.split("/").filter((p) => p.length > 0);
  return { type, slug };
}

export function buildCustomizedURL({
  config: _config,
  urlBase,
  urlSuffix,
  urlSimplified = false,
  addCharityType = isTwitchEnabled,
  addTiltifyCampaign = true,
}: {
  config: Record<string, unknown>;
  urlBase: string;
  urlSuffix?: string;
  urlSimplified?: boolean;
  addCharityType?: boolean;
  addTiltifyCampaign?: boolean;
}) {
  const config = _config as Record<string, string>;
  const {
    charityType,
    tiltifyUsername,
    tiltifyUserSlug,
    twitchUsername,
    twitchCampaignID,
    tiltifyCampaignSlug,
    tiltifyIsTeamCampaign,
    tiltifyTeamSlug,
  } = config;

  const baseUri = import.meta.env.VITE_OVERLAY_URI || "https://plo.dev.experience.stjude.org";

  // Parse urlBase into category + overlayName
  let category: OverlayCategory | undefined;
  let overlayName: string;
  const categories: OverlayCategory[] = [
    "overlays",
    "progress-bars",
    "dashboards",
    "displays",
    "bac",
    "gcx",
  ];
  const parts = urlBase.split("/").filter(Boolean);

  if (parts.length >= 2 && categories.includes(parts[0] as OverlayCategory)) {
    category = parts[0] as OverlayCategory;
    overlayName = parts.slice(1).join("/");
  } else {
    overlayName = urlBase;
  }

  // Build the target
  let target: OverlayTarget | undefined;
  if (!urlSimplified) {
    if (charityIsTiltify(charityType)) {
      if (tiltifyIsTeamCampaign && tiltifyTeamSlug) {
        target = {
          charityType: "tiltify",
          teamSlug: tiltifyTeamSlug,
          campaignSlug: addTiltifyCampaign ? tiltifyCampaignSlug : undefined,
        };
      } else {
        target = {
          charityType: "tiltify",
          userSlug: tiltifyUserSlug ?? tiltifyUsername ?? "",
          campaignSlug: addTiltifyCampaign ? tiltifyCampaignSlug : undefined,
        };
      }
    } else {
      target = {
        charityType: "twitch",
        twitchUsername: twitchUsername ?? "",
        twitchCampaignID: twitchCampaignID,
      };
    }
  }

  // Use the new path builder but apply params with legacy flat behavior
  // (the old code applied ALL config keys regardless of overlay type)
  return buildOverlayURL({
    baseURL: baseUri,
    category,
    overlayName,
    target,
    simplified: urlSimplified,
    includeCharityType: addCharityType,
    includeCampaignSlug: addTiltifyCampaign,
    suffix: urlSuffix,
    rawParams: buildLegacyParams(config),
  });
}

/**
 * Maps the legacy flat config to URL search params, preserving the exact
 * behavior of the old monolithic buildCustomizedURL.
 */
function buildLegacyParams(config: Record<string, string>): Record<string, string> {
  const params: Record<string, string> = {};

  const {
    bgColor,
    backgroundOpacity,
    textColor,
    textSize,
    showScroller,
    showChallenges,
    showRewards,
    showDonors,
    showGivingInstructions,
    givingInstructionsFrequency,
    showOriginalGoal,
    showGoalAmount,
    scrollerPosition,
    width,
    position,
    verticalType,
    thermColor,
    amount,
    demoMode,
  } = config;

  if (demoMode) {
    params.demoMode = demoMode;
  }
  if (bgColor) {
    params.bgColor = bgColor;
  }
  if (backgroundOpacity) {
    params.backgroundOpacity = backgroundOpacity;
  }
  if (textColor) {
    params.textColor = textColor;
  }
  if (textSize) {
    params.textSize = textSize;
  }
  if (showRewards) {
    params.showRewards = showRewards;
  }
  if (showDonors) {
    params.showDonors = showDonors;
  }
  // Giving instructions are default-on in the overlay, so the opt-out has to be
  // encoded explicitly; omitting the param would silently re-enable them.
  if (showGivingInstructions) {
    params.showGivingInstructions = showGivingInstructions;
  }
  if (isYes(showGivingInstructions)) {
    const frequency = Number(givingInstructionsFrequency);
    if (Number.isFinite(frequency) && frequency > 0) {
      params.givingInstructionsFrequency = String(frequency);
    }
  }
  if (showScroller) {
    params.showScroller = showScroller;
  }
  if (showChallenges) {
    params.showChallenges = showChallenges;
  }
  if (showOriginalGoal) {
    params.showOriginalGoal = showOriginalGoal;
  }
  if (showGoalAmount) {
    params.showGoalAmount = showGoalAmount;
  }
  if (scrollerPosition) {
    params.scrollerPosition = scrollerPosition;
  }
  if (amount) {
    params.amount = `${amount}`.replace(".00", "");
  }
  if (width) {
    params.width = width;
  }
  if (position) {
    params.position = position;
  }
  if (verticalType) {
    params.type = verticalType;
  }

  // Event List Specific
  const { color, orientation, align, listAmount } = config;
  if (color) {
    params.color = color;
  }
  if (orientation) {
    params.orientation = orientation;
  }
  if (align) {
    params.align = align;
  }
  if (listAmount) {
    params.listAmount = listAmount;
  }

  // Leaderboard Specific
  const { leaderboardColor, leaderDonors, timeType, hideBadge, minimalBackground, fullWidth } =
    config;
  if (leaderboardColor) {
    params.bgColor = leaderboardColor;
  }
  if (leaderDonors) {
    params.topDonors = leaderDonors;
  }
  if (timeType) {
    params.timeType = timeType;
  }
  if (hideBadge === "yes") {
    params.hideBadge = "yes";
  }
  if (minimalBackground === "yes") {
    params.minimalBackground = "yes";
  }
  if (fullWidth === "yes") {
    params.fullWidth = "yes";
  }

  // Rewards Specific
  const {
    rewardsTheme,
    rewardsBackgroundColor,
    highlightFeaturedRewards,
    showQuantityClaimed,
    showAllRewards,
  } = config;
  if (rewardsTheme) {
    params.theme = rewardsTheme;
  }
  if (rewardsBackgroundColor) {
    params.bgColor = rewardsBackgroundColor;
  }
  if (highlightFeaturedRewards) {
    params.highlightFeaturedRewards = highlightFeaturedRewards;
  }
  if (showQuantityClaimed) {
    params.showQuantityClaimed = showQuantityClaimed;
  }
  if (showAllRewards) {
    params.showAllRewards = showAllRewards;
  }

  // Previous Totals Specific
  const {
    previousTotalsOverlayTheme,
    previousTotalsOverlayBackgroundColor,
    previousTotalsOverlayMaxVisible,
  } = config;
  if (previousTotalsOverlayTheme) {
    params.theme = previousTotalsOverlayTheme;
  }
  if (previousTotalsOverlayBackgroundColor) {
    params.bgColor = previousTotalsOverlayBackgroundColor;
  }
  if (previousTotalsOverlayMaxVisible) {
    params.maxVisible = previousTotalsOverlayMaxVisible;
  }

  // Timers
  const { displayTimer } = config;
  if (displayTimer) {
    params.displayTimer = displayTimer;
  }

  // Subathon timer specific
  const { intensity, glowEffect, glowEffectMatches } = config;
  if (intensity) {
    params.intensity = intensity;
  }
  if (glowEffect) {
    params.glowEffect = glowEffect;
  }
  if (glowEffectMatches) {
    params.glowEffectMatches = glowEffectMatches;
  }

  // Polls specific
  const {
    pollsProgressBarColor,
    pollsType,
    pollsShowDollars,
    pollsShowPercentage,
    pollsShowEndsAt,
    pollsEndsAtDisplayMode,
    pollsRanking,
  } = config;
  if (pollsProgressBarColor) {
    params.progressBarColor = pollsProgressBarColor;
  }
  if (pollsType) {
    params.type = pollsType;
  }
  if (pollsShowDollars) {
    params.showDollars = pollsShowDollars;
  }
  if (pollsShowPercentage) {
    params.showPercentage = pollsShowPercentage;
  }
  if (pollsShowEndsAt) {
    params.showEndsAt = pollsShowEndsAt;
  }
  if (pollsEndsAtDisplayMode) {
    params.endsAtDisplayMode = pollsEndsAtDisplayMode;
  }
  if (pollsRanking) {
    params.pollRanking = pollsRanking;
  }

  // Alerts specific
  const { incentives, minDono, minAudio, sound, volume } = config;
  if (minDono) {
    params["min-donation"] = minDono;
  }
  if (minAudio) {
    params.minAudio = minAudio;
  }
  if (volume) {
    params.volume = volume;
  }
  if (sound) {
    params.sound = sound;
  }
  if (incentives) {
    params.incentives = incentives;
  }

  // TTS specific
  const { pitch, selectedVoice, pollID, usePoll, pollMapping, autoStart } = config;
  if (pitch) {
    params.pitch = pitch;
  }
  if (autoStart) {
    params.autoStart = autoStart;
  }
  if (isYes(usePoll) && pollID && pollID.length > 0 && pollMapping) {
    params.usePoll = usePoll;
    params.pollMapping = pollMapping;
    params.pollID = pollID;
  }
  if (selectedVoice && (!usePoll || !isYes(usePoll))) {
    params.selectedVoice = selectedVoice;
  }

  // Milestone specific
  const { visual } = config;
  if (visual) {
    params.visual = visual;
  }

  // Schedule specific
  const { nowColor, barHeight, showClock, maxShow, hostsFromName, roundedCorners, slideDirection } =
    config;
  if (nowColor) {
    params.nowColor = nowColor;
  }
  if (barHeight) {
    params.barHeight = barHeight;
  }
  if (showClock) {
    params.showClock = showClock;
  }
  if (maxShow) {
    params.maxShow = maxShow;
  }
  if (hostsFromName) {
    params.hostsFromName = hostsFromName;
  }
  if (roundedCorners) {
    params.roundedCorners = roundedCorners;
  }
  if (slideDirection) {
    params.slideDirection = slideDirection;
  }

  // Total specific
  const { eventID } = config;
  if (eventID) {
    params.eventID = eventID;
  }

  const { startTime, endTime } = config;
  if (startTime) {
    params.startTime = startTime;
  }
  if (endTime) {
    params.endTime = endTime;
  }

  // Donor List
  const {
    donorListBackgroundColor,
    donorListDisplayIncentives,
    donorListOrientation,
    donorListAlign,
    donorListAmount,
  } = config;
  if (donorListBackgroundColor) {
    params.bgColor = donorListBackgroundColor;
  }
  if (donorListDisplayIncentives) {
    params.displayIncentives = donorListDisplayIncentives;
  }
  if (donorListOrientation) {
    params.orientation = donorListOrientation;
  }
  if (donorListAlign) {
    params.alignment = donorListAlign;
  }
  if (donorListAmount) {
    params.showDonors = donorListAmount;
  }

  const { minDonation, highlight, donorMin } = config;
  if (minDonation) {
    params.minDonation = minDonation;
  }
  if (highlight) {
    params.highlight = highlight;
  }
  if (donorMin) {
    params.donorMin = donorMin;
  }

  // Fundraising / List dashboard
  const { showUserAmountRaised, streamerName } = config;
  if (showUserAmountRaised) {
    params.showUserAmountRaised = showUserAmountRaised;
  }
  if (streamerName) {
    params.streamerName = streamerName;
  }

  const { giftsThatGiveAmount } = config;
  if (giftsThatGiveAmount) {
    params.amount = giftsThatGiveAmount;
  }

  // Ball Pit
  const {
    minAmount,
    maxAmount,
    maxBalls,
    maxScale,
    randomColors,
    scaleSize,
    showNames,
    startSize,
    clampVelocity,
    clampAngularSpeed,
    explosionForceMagnitude,
  } = config;
  if (minAmount !== undefined) {
    params.minAmount = minAmount;
  }
  if (maxAmount !== undefined) {
    params.maxAmount = maxAmount;
  }
  if (maxBalls !== undefined) {
    params.maxBalls = maxBalls;
  }
  if (maxScale !== undefined) {
    params.maxScale = maxScale;
  }
  if (clampVelocity !== undefined && Number(clampVelocity) !== 12) {
    params.clampVelocity = clampVelocity;
  }
  if (randomColors !== undefined) {
    params.randomColors = randomColors;
  }
  if (scaleSize !== undefined) {
    params.scaleSize = scaleSize;
  }
  if (showNames !== undefined) {
    params.showNames = showNames;
  }
  if (startSize !== undefined) {
    params.startSize = startSize;
  }
  if (clampAngularSpeed !== undefined && Number(clampAngularSpeed) !== 0.03) {
    params.clampAngularSpeed = clampAngularSpeed;
  }
  if (explosionForceMagnitude !== undefined && Number(explosionForceMagnitude) !== 0.025) {
    params.explosionForceMagnitude = explosionForceMagnitude;
  }

  const { ballType } = config;
  if (ballType !== undefined) {
    params.ballType = ballType;
  }

  const { debugMode, showAllDonations, hideAnonymous } = config;

  if (debugMode !== undefined && debugMode === "yes") {
    params.debugMode = "yes";
  }
  if (showAllDonations !== undefined && showAllDonations === "yes") {
    params.showAllDonations = "yes";
  }

  if (hideAnonymous !== undefined && hideAnonymous === "yes") {
    params.hideAnonymous = "yes";
  }

  const { allowNegative, alignment } = config;
  if (allowNegative !== undefined) {
    params.allowNegative = allowNegative;
  }
  if (alignment !== undefined) {
    params.alignment = alignment;
  }

  const { type } = config;
  if (type) {
    params.type = type;
  }

  // Donation Train
  const {
    donationTrainTheme,
    donationTrainPosition,
    donationTrainMaxVisible,
    donationTrainColor,
    donationTrainShowActivationMessage,
  } = config;
  if (donationTrainColor) {
    params.bgColor = donationTrainColor;
  }
  if (donationTrainTheme) {
    params.theme = donationTrainTheme;
  }
  if (donationTrainPosition) {
    params.position = donationTrainPosition;
  }
  if (donationTrainMaxVisible) {
    params.maxTrainsVisible = donationTrainMaxVisible;
  }
  if (donationTrainShowActivationMessage) {
    params.showActivationMessage = donationTrainShowActivationMessage;
  }

  // Time Block
  //
  // Prefixed config keys because the flat legacy namespace already spends
  // `theme` / `alignment` / `bgColor` on other overlays. Assigned after those
  // generic mappings above so the time-block values win when both are present.
  const {
    timeBlock,
    timeBlockLabel,
    timeBlockName,
    timeBlockTheme,
    timeBlockAlignment,
    timeBlockBackgroundColor,
    timeBlockShowTimeRange,
    timeBlockShowLiveIndicator,
    timeBlockShowCents,
    timeBlockScale,
  } = config;
  if (timeBlock) {
    params.timeBlock = timeBlock;
  }
  if (timeBlockLabel) {
    params.label = timeBlockLabel;
  }
  if (timeBlockName) {
    params.blockName = timeBlockName;
  }
  if (timeBlockTheme) {
    params.theme = timeBlockTheme;
  }
  if (timeBlockAlignment) {
    params.alignment = timeBlockAlignment;
  }
  if (timeBlockBackgroundColor) {
    params.bgColor = timeBlockBackgroundColor;
  }
  // These two default ON in the overlay, so only the opt-out is worth encoding.
  if (timeBlockShowTimeRange === "no") {
    params.showTimeRange = "no";
  }
  if (timeBlockShowLiveIndicator === "no") {
    params.showLiveIndicator = "no";
  }
  // Cents default OFF.
  if (timeBlockShowCents === "yes") {
    params.showCents = "yes";
  }
  if (timeBlockScale && Number(timeBlockScale) !== 1) {
    params.scale = timeBlockScale;
  }

  if ("ccamMode" in config && config.ccamMode === "yes") {
    delete params.bgColor;
    params.ccamMode = "yes";
  }
  if ("ccamFlagVerticalPosition" in config && config.ccamFlagVerticalPosition !== "bottom") {
    params.ccamFlagVerticalPosition = config.ccamFlagVerticalPosition;
  }
  if ("ccamFlagHorizontalPosition" in config && config.ccamFlagHorizontalPosition !== "right") {
    params.ccamFlagHorizontalPosition = config.ccamFlagHorizontalPosition;
  }

  // Team progress
  const { showTeamProgress, teamPosition, teamBackgroundColor } = config;
  if (showTeamProgress !== undefined) {
    params.showTeamProgress = showTeamProgress;
  }
  if (teamPosition !== undefined) {
    params.teamPosition = teamPosition;
  }
  if (teamBackgroundColor !== undefined) {
    params.teamBgColor = teamBackgroundColor;
  }

  // Spotlight / Leaderboard dashboard
  const {
    showDonorOfTheHour,
    showDonorOfTheDay,
    showCommunityHero,
    live,
    rotateTimer,
    backgroundColor,
  } = config;
  if (showDonorOfTheHour !== undefined) {
    params.showDonorOfTheHour = showDonorOfTheHour;
  }
  if (showDonorOfTheDay !== undefined) {
    params.showDonorOfTheDay = showDonorOfTheDay;
  }
  if (showCommunityHero !== undefined) {
    params.showCommunityHero = showCommunityHero;
  }
  if (live !== undefined) {
    params.live = live;
  }
  if (rotateTimer !== undefined) {
    params.rotateTimer = `${rotateTimer}`;
  }
  if (backgroundColor !== undefined) {
    params.bgColor = `${backgroundColor}`;
  }
  if (bgColor !== undefined) {
    params.bgColor = `${bgColor}`;
  }
  // `thermColor` is write-only: no overlay has ever read it — every progress
  // bar reads `bgColor`, including `vertical`, the only one overlay-core's
  // registry ever offered a "Thermometer fill color" for. Emitting it produced
  // a link that silently rendered `default-red`. Folded in as a last-resort
  // fallback rather than dropped, so a config carrying only the old field
  // still colours the bar. Runs after the two assignments above so anything
  // explicit wins.
  if (params.bgColor === undefined && thermColor) {
    params.bgColor = thermColor;
  }

  const { maxMessages, usernameColor, mentionColor, fontSize, theme, messageBubbleColor } = config;
  if (maxMessages !== undefined) {
    params.maxMessages = maxMessages;
  }
  if (usernameColor !== undefined) {
    params.usernameColor = usernameColor;
  }
  if (mentionColor !== undefined) {
    params.mentionColor = mentionColor;
  }
  if (fontSize !== undefined) {
    params.fontSize = fontSize;
  }
  if (theme !== undefined) {
    params.theme = theme;
  }
  if (messageBubbleColor !== undefined) {
    params.messageBubbleColor = messageBubbleColor;
  }

  const { showSchedule, schedulePosition, scheduleBackgroundColor, scheduleMode } = config;
  if (showSchedule !== undefined) {
    params.showSchedule = showSchedule;
  }
  if (schedulePosition !== undefined) {
    params.schedulePosition = schedulePosition;
  }
  if (scheduleBackgroundColor !== undefined) {
    params.scheduleBgColor = scheduleBackgroundColor;
  }
  if (scheduleMode !== undefined) {
    params.scheduleMode = scheduleMode;
  }

  return params;
}

export function formatColorName(colorName: string) {
  const formatted = colorName === "default-red" ? "red" : `${colorName.replace(/-/g, " ")}`;
  return formatted.replace(/(^|\s)\S/g, (t) => t.toUpperCase());
}

export function calculateGiftsThatGiveGoal(rawGoal: number | string) {
  const goal = parseInt(`${rawGoal}`.split(".")[0], 10);

  if (goal < 100) {
    return 0;
  } else if (goal < 500) {
    return 100;
  } else if (goal < 1000) {
    return 500;
  } else if (goal < 1500) {
    return 1000;
  } else if (goal < 2000) {
    return 1500;
  } else if (goal < 3000) {
    return 2000;
  } else if (goal < 5000) {
    return 3000;
  } else if (goal < 7500) {
    return 5000;
  } else if (goal < 10000) {
    return 7500;
  } else if (goal < 20000) {
    return 10000;
  } else if (goal < 30000) {
    return 20000;
  } else if (goal < 40000) {
    return 30000;
  } else if (goal < 50000) {
    return 40000;
  } else if (goal < 60000) {
    return 50000;
  } else if (goal < 70000) {
    return 60000;
  } else if (goal < 75000) {
    return 70000;
  } else if (goal < 80000) {
    return 75000;
  } else if (goal < 90000) {
    return 80000;
  } else if (goal < 100000) {
    return 90000;
  } else {
    return 100000;
  }
}

export function isCurrentMonthCCAM() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  return currentMonth === 9;
}
